import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loginStart, loginSuccess, loginFailure, logout, clearError, updateProfileSuccess } from '../store/reducers/auth';
import { login as loginApi, logout as logoutApi, updateProfile as updateProfileApi, changePassword as changePasswordApi } from '../services/authApi';

// ==============================|| AUTH HOOK ||============================== //

export const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const login = async (credentials) => {
    try {
      dispatch(loginStart());
      const response = await loginApi(credentials);
      // response.data contains { user, sessionId, expiresAt, authType }
      // Tokens live in httpOnly cookies — only user data goes into Redux
      dispatch(loginSuccess({
        user: response.data.user
      }));
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch(loginFailure(message));
      throw error;
    }
  };

  const logoutUser = async () => {
    try {
      // Call the backend — this clears the httpOnly cookies (adminAccessToken + adminRefreshToken)
      // The cookie-clear ONLY works server-side via res.clearCookie()
      // Just dispatching Redux would clear state but leave cookies alive in the browser
      await logoutApi();
    } catch (error) {
      // Even if the API call fails (network error, session already expired),
      // we still clear local state so the user isn't stuck
      console.warn('Logout API call failed, clearing local state anyway:', error.message);
    } finally {
      // Always clear Redux state regardless of API outcome
      dispatch(logout());
    }
  };

  const clearAuthError = () => {
    dispatch(clearError());
  };

  const updateProfile = useCallback(async (updatedUser) => {
    const response = await updateProfileApi(updatedUser);
    if (response.success) {
      dispatch(updateProfileSuccess(response.data));
    }
    return response;
  }, [dispatch]);

  const changePassword = useCallback(async (passwordData) => {
    return await changePasswordApi(passwordData);
  }, []);

  const hasRole = (role) => {
    return auth.user?.role === role;
  };

  const hasPermission = (permission) => {
    return auth.user?.permissions?.includes(permission) ||
           auth.user?.permissions?.includes('*');
  };

  return {
    ...auth,
    login,
    logout: logoutUser,
    clearError: clearAuthError,
    updateProfile,
    changePassword,
    hasRole,
    hasPermission
  };
};